/**
 * <s-product-thumbs>
 * Links the thumbnail rail to the product gallery.
 *   [data-product-gallery]   — the slide track: a sideways scroll-snap row showing one
 *                              main photo at a time on every screen size
 *   [data-gallery-slide]     — each slide inside the track
 *   [data-thumb-rail]        — the thumbnail list (a row on mobile, a scrolling column
 *                              of about five beside the photo on desktop)
 *   [data-thumb-index]       — thumbnail buttons; value is the slide index
 * Clicking a thumbnail swaps the main photo to that slide; whichever slide is showing
 * marks its thumbnail aria-current and the rail scrolls to keep it visible.
 */
class SProductThumbs extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-product-gallery]');
    this.rail = this.querySelector('[data-thumb-rail]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
    if (!this.track || this.slides.length < 2) return;

    this.onClick = (event) => {
      const thumb = event.target.closest('[data-thumb-index]');
      if (!thumb) return;
      const index = Number(thumb.dataset.thumbIndex);
      const slide = this.slides[index];
      if (!slide) return;
      const behavior = this.reducedMotion() ? 'auto' : 'smooth';
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior });
      this.setCurrent(index);
    };
    this.addEventListener('click', this.onClick);

    // Up/down arrows page the thumbnail rail; they hide when every thumbnail fits and dim at either end.
    this.arrows = [...this.querySelectorAll('[data-thumb-scroll]')];
    this.onArrow = (event) => {
      const arrow = event.target.closest('[data-thumb-scroll]');
      if (!arrow || !this.rail) return;
      const step = (this.rail.clientHeight || 0) * 0.8 * Number(arrow.dataset.thumbScroll);
      this.rail.scrollBy({ top: step, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
    };
    this.arrows.forEach((arrow) => arrow.addEventListener('click', this.onArrow));
    this.onRailScroll = () => this.updateArrows();
    this.rail?.addEventListener('scroll', this.onRailScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.updateArrows());
    if (this.rail) this.resizeObserver.observe(this.rail);
    this.updateArrows();

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.rail?.removeEventListener('scroll', this.onRailScroll);
    this.arrows?.forEach((arrow) => arrow.removeEventListener('click', this.onArrow));
  }

  updateArrows() {
    if (!this.rail || !this.arrows?.length) return;
    const rail = this.rail;
    const scrollable = rail.scrollHeight > rail.clientHeight + 1;
    this.arrows.forEach((arrow) => {
      arrow.hidden = !scrollable;
      const direction = Number(arrow.dataset.thumbScroll);
      const atEnd =
        direction < 0 ? rail.scrollTop <= 1 : rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 1;
      arrow.disabled = atEnd;
    });
  }

  reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  setCurrent(index) {
    if (index < 0) return;
    this.thumbs.forEach((thumb) => {
      if (Number(thumb.dataset.thumbIndex) === index) {
        thumb.setAttribute('aria-current', 'true');
        this.revealThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  // Scrolls only the rail (never the page) so the current thumbnail stays visible. The rail is position: relative, so offsets are measured from it.
  revealThumb(thumb) {
    if (!this.rail) return;
    const item = thumb.closest('li') ?? thumb;
    const rail = this.rail;
    const behavior = this.reducedMotion() ? 'auto' : 'smooth';
    const vertical = rail.scrollHeight > rail.clientHeight + 1;
    if (vertical) {
      const top = item.offsetTop;
      if (top < rail.scrollTop) rail.scrollTo({ top, behavior });
      else if (top + item.offsetHeight > rail.scrollTop + rail.clientHeight)
        rail.scrollTo({ top: top + item.offsetHeight - rail.clientHeight, behavior });
    } else if (rail.scrollWidth > rail.clientWidth + 1) {
      const left = item.offsetLeft;
      if (left < rail.scrollLeft) rail.scrollTo({ left, behavior });
      else if (left + item.offsetWidth > rail.scrollLeft + rail.clientWidth)
        rail.scrollTo({ left: left + item.offsetWidth - rail.clientWidth, behavior });
    }
  }
}

if (!customElements.get('s-product-thumbs')) customElements.define('s-product-thumbs', SProductThumbs);
